import { useEffect, useState } from 'react';
import {
  SUPPORT_CATEGORIES,
  SUPPORT_CATEGORY_NAMES,
  SUPPORT_MESSAGE_MAX,
  SUPPORT_MESSAGE_MIN,
  type ListMyServersResponse,
  type MeResponse,
  type SupportCategory,
  type SupportReceipt,
} from '@hearth/shared';
import { useAuth } from '../auth/AuthProvider';
import { CONTACT_EMAIL } from '../legal/SiteFooter';
import { linkTo, navigate } from '../router';
import { Shell } from './Shell';
import { errorText } from './serverWords';

// /support (go live, S3), as the canvas board: signed in, a form (what it's about, which server,
// the message) sent to POST /v1/support, then "Thanks, we've got it"; signed out, sign in, or the
// contact email (complaints and privacy requests must be possible without an account).

/** What's wrong with the message, once they try to send it; the API's own words. */
export function messageProblem(message: string): string {
  return message.trim().length < SUPPORT_MESSAGE_MIN ? `Write at least ${SUPPORT_MESSAGE_MIN} characters` : '';
}

/** Where the reply goes: their account's email, or (a sign-in that shared none) a nudge to include one. */
export function replyLine(email: string | undefined): string {
  return email
    ? `We'll reply to ${email}, usually within 2 working days.`
    : "Your sign-in didn't share an email address, so add one to your message if you'd like a reply.";
}

export function Support() {
  const { status, api, signOut } = useAuth();
  const [me, setMe] = useState<MeResponse>();
  const [servers, setServers] = useState<{ serverId: string; name: string }[]>([]);
  const [category, setCategory] = useState<SupportCategory>('feedback');
  const [serverId, setServerId] = useState('');
  const [message, setMessage] = useState('');
  const [touched, setTouched] = useState(false);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (status !== 'signed-in' || !api) return;
    api.get<MeResponse>('/v1/me').then(setMe, (err: unknown) => setError(errorText(err)));
    // The server picker: theirs, by name; none to pick is fine (it's optional).
    api.get<ListMyServersResponse>('/v1/servers').then((r) => setServers(r.servers.map((s) => ({ serverId: s.serverId, name: s.name }))), () => undefined);
  }, [status, api]);

  async function send() {
    setTouched(true);
    if (!api || messageProblem(message)) return;
    setBusy(true);
    setError('');
    try {
      await api.post<SupportReceipt>('/v1/support', { category, message, ...(serverId ? { serverId } : {}) });
      setSent(true);
      setMessage('');
      setServerId('');
      setTouched(false);
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }

  return (
    <Shell me={me} api={api} onSignOut={signOut} home={status === 'signed-in' ? '/app' : '/'}>
      <SupportView
        status={status === 'signed-in' ? (sent ? 'sent' : 'form') : status === 'signed-out' ? 'signed-out' : 'loading'}
        email={me?.email}
        servers={servers}
        category={category}
        serverId={serverId}
        message={message}
        touched={touched}
        busy={busy}
        error={error}
        onCategory={setCategory}
        onServer={setServerId}
        onMessage={setMessage}
        onSend={() => void send()}
        onAgain={() => setSent(false)}
        onSignIn={() => navigate(`/signin?returnTo=${encodeURIComponent('/support')}`)}
      />
    </Shell>
  );
}

/** The page, given where things stand (no fetching: easy to test). */
export function SupportView(props: {
  status: 'loading' | 'signed-out' | 'form' | 'sent';
  email?: string | undefined;
  servers: { serverId: string; name: string }[];
  category: SupportCategory;
  serverId: string;
  message: string;
  touched: boolean;
  busy: boolean;
  error: string;
  onCategory: (category: SupportCategory) => void;
  onServer: (serverId: string) => void;
  onMessage: (message: string) => void;
  onSend: () => void;
  onAgain: () => void;
  onSignIn: () => void;
}) {
  const { status, email, servers, category, serverId, message, touched, busy, error } = props;
  const problem = touched ? messageProblem(message) : '';
  const mail = <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>;
  return (
    <div className="support">
      <div className="page-head">
        <h1 className="page-title">Get in touch</h1>
      </div>
      {error && <p className="notice" role="alert">{error}</p>}

      {status === 'loading' && <p className="quiet" aria-live="polite">Loading…</p>}

      {status === 'form' && (
        <form
          className="panel support-form"
          onSubmit={(e) => {
            e.preventDefault();
            props.onSend();
          }}
        >
          <label className="support-field">
            <span>What's it about?</span>
            <select className="field" value={category} disabled={busy} onChange={(e) => props.onCategory(e.target.value as SupportCategory)}>
              {SUPPORT_CATEGORIES.map((c) => (
                <option key={c} value={c}>{SUPPORT_CATEGORY_NAMES[c]}</option>
              ))}
            </select>
          </label>
          <label className="support-field">
            <span>
              Which server? <span className="quiet">(if applicable)</span>
            </span>
            <select className="field" value={serverId} disabled={busy} onChange={(e) => props.onServer(e.target.value)}>
              <option value="">None</option>
              {servers.map((s) => (
                <option key={s.serverId} value={s.serverId}>{s.name}</option>
              ))}
            </select>
          </label>
          <label className="support-field">
            <span>Message</span>
            <textarea
              className="field support-message"
              rows={7}
              maxLength={SUPPORT_MESSAGE_MAX}
              value={message}
              disabled={busy}
              placeholder="What happened, and what did you expect?"
              aria-invalid={problem ? true : undefined}
              aria-describedby={problem ? 'message-problem' : undefined}
              onChange={(e) => props.onMessage(e.target.value)}
            />
          </label>
          {problem && <p id="message-problem" className="field-problem">{problem}</p>}
          <p className="quiet">{replyLine(email)}</p>
          <div>
            <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send'}</button>
          </div>
        </form>
      )}

      {status === 'sent' && (
        <section className="join-card" aria-live="polite">
          <h2>Thanks, we've got it</h2>
          <p className="quiet">{replyLine(email)}</p>
          <div className="support-actions">
            <a className="btn btn-primary" {...linkTo('/app')}>Your servers</a>
            <button className="btn" type="button" onClick={props.onAgain}>Send another</button>
          </div>
        </section>
      )}

      {status === 'signed-out' && (
        <section className="panel support-form">
          <p>Sign in so we know which account and servers you need help with.</p>
          <div>
            <button className="btn btn-primary" type="button" onClick={props.onSignIn}>Sign in</button>
          </div>
          <p className="quiet">Can't sign in, or not on Hearth? Email {mail}, privacy requests included.</p>
        </section>
      )}
    </div>
  );
}
